// client/src/coms/views/games_parts/WatchActivityView.js
// A Watch Together session opened from Activities. The player itself is the existing Watch Together view
// (queue, synced playback, providers, host controls, permissions) running over the session's server relay,
// so nobody needs to be in a voice channel. Around it: who is here (host, owner, connected), the invite
// code and link, the host's session controls, and the optional voice channel the session belongs to.
import React, { useEffect, useMemo, useState } from 'react';
import WatchTogetherView from '../WatchTogetherView';
import UserAvatar from './UserAvatar';
import { InviteBox } from './LobbyParts';
import useWatchActivity, { describeWatchError, isInVoiceChannel } from './useWatchActivity';

const CONNECTION_TEXT = { connecting: 'Connecting…', reconnecting: 'Reconnecting…', closed: 'Disconnected' };

function ParticipantRow({ p, meId, canManage, onTransfer, onKick, busy }) {
  const me = p.userId === meId;
  return (
    <li className="vox-watch-person" data-testid={`watch-person-${p.userId}`}>
      <span className={`vox-watch-dot ${p.connected ? 'on' : ''}`} title={p.connected ? 'Watching now' : 'Away'} aria-hidden="true" />
      <UserAvatar userId={p.userId} name={p.name} size={26} />
      <span className="vox-watch-person-name">
        {p.name}{me ? ' (you)' : ''}
        <span className="vox-watch-person-tags">
          {p.host ? <span className="vox-watch-tag host" data-testid="watch-host-badge">👑 Host</span> : null}
          {p.owner && !p.host ? <span className="vox-watch-tag">Started it</span> : null}
          {p.joinedVia === 'code' ? <span className="vox-watch-tag">Invite code</span> : p.joinedVia === 'channel' ? <span className="vox-watch-tag">Voice channel</span> : null}
          {!p.connected ? <span className="vox-watch-tag dim">Away</span> : null}
        </span>
      </span>
      {canManage && !me ? (
        <span className="vox-watch-person-actions">
          {!p.host ? <button type="button" className="party-btn small ghost" disabled={busy} onClick={() => onTransfer(p.userId)} data-testid={`watch-make-host-${p.userId}`}>Make host</button> : null}
          {!p.owner ? <button type="button" className="party-btn small ghost danger" disabled={busy} onClick={() => onKick(p.userId)} data-testid={`watch-kick-${p.userId}`}>Remove</button> : null}
        </span>
      ) : null}
    </li>
  );
}

export default function WatchActivityView({ activityId, user, voiceSession, onJoinVoice, onBack, onExit, onActivity }) {
  const meId = Number(user && user.id) || 0;
  const watch = useWatchActivity(activityId);
  const { activity, room, gone, connection } = watch;
  const [panel, setPanel] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 900 : true));
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (gone) onExit(describeWatchError(gone, 'This watch session is no longer available.'));
  }, [gone, onExit]);
  useEffect(() => {
    if (activity && onActivity) onActivity(activity);
  }, [activity, onActivity]);

  // The player keys its shared state by this id; it must stay the same object while the session lasts.
  const sessionKey = activity ? activity.id : '';
  const sessionTitle = activity ? activity.title : '';
  const playerServer = useMemo(() => (sessionKey ? { id: `watch-${sessionKey}`, name: sessionTitle } : null), [sessionKey, sessionTitle]);

  if (!activity) return <div className="vox-watch-loading" data-testid="watch-loading">Joining Watch Together…</div>;

  const you = activity.you || {};
  const isHost = !!you.isHost;
  const canManage = isHost || !!you.isOwner;
  const participants = activity.participants || [];
  const here = participants.filter((p) => p.connected).length;
  const inVoice = isInVoiceChannel(voiceSession, activity);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <div className="vox-watch-bar" data-testid="watch-activity-bar">
      <button type="button" className="coms-mini-btn" onClick={onBack}>← Activities</button>
      <div className="vox-watch-title">
        <div className="vox-watch-name" data-testid="watch-title">📺 {activity.title}</div>
        <div className="vox-watch-sub">
          {activity.hostName ? <>hosted by <strong>{activity.hostName}</strong></> : null}
          {activity.voice ? (
            <span className="vox-watch-voice" data-testid="watch-voice-chip" title="Started in this voice channel. People there can join from Activities; anyone can join with the code.">
              🔊 {activity.voice.channelName}{activity.voice.serverName ? ` · ${activity.voice.serverName}` : ''}
            </span>
          ) : <span className="vox-watch-voice muted">No voice channel needed</span>}
          {activity.controlMode === 'everyone' ? <span className="vox-watch-voice">Everyone controls playback</span> : null}
          {connection !== 'connected' ? <span className="vox-watch-conn" role="status" data-testid="watch-connection">{CONNECTION_TEXT[connection] || connection}</span> : null}
        </div>
      </div>
      <button type="button" className="vox-watch-people-btn" onClick={() => setPanel((v) => !v)} aria-expanded={panel} data-testid="watch-participants" title={participants.map((p) => p.name).join(', ')}>
        <span className="vox-watch-avatars">{participants.slice(0, 5).map((p) => <UserAvatar key={p.userId} userId={p.userId} name={p.name} size={22} />)}</span>
        <span>{here} watching · Invite</span>
      </button>
      {activity.voice && !inVoice && typeof onJoinVoice === 'function' ? (
        <button type="button" className="coms-mini-btn" onClick={() => onJoinVoice(activity.voice.serverPublicId, activity.voice.channelId)} data-testid="watch-join-voice" title="Optional: talk while you watch">
          Join voice
        </button>
      ) : null}
      <button type="button" className="coms-mini-btn" data-testid="watch-leave" onClick={() => run(async () => { await watch.leave(); onExit(''); })} disabled={busy}>
        Leave
      </button>
    </div>
  );

  return (
    <div className="vox-watch-activity" data-testid="watch-activity" data-host={isHost ? '1' : '0'}>
      {header}
      {watch.error ? <div role="alert" className="party-error" style={{ margin: '8px 16px 0' }}>{watch.error}</div> : null}
      <div className={`vox-watch-body ${panel ? 'with-panel' : ''}`}>
        <div className="vox-watch-main">
          {room && room.localParticipant ? (
            <WatchTogetherView server={playerServer} user={user} voiceRoom={room} voiceSession={voiceSession} />
          ) : (
            <div className="vox-watch-loading">{connection === 'closed' ? 'Disconnected from the session.' : 'Connecting to the session…'}</div>
          )}
        </div>
        {panel ? (
          <aside className="vox-watch-panel" aria-label="Watch session">
            <InviteBox code={activity.code} />
            <section>
              <div className="party-kicker">In this session · {participants.length}</div>
              <ul className="vox-watch-people" data-testid="watch-people">
                {participants.map((p) => (
                  <ParticipantRow
                    key={p.userId}
                    p={p}
                    meId={meId}
                    canManage={canManage}
                    busy={busy}
                    onTransfer={(id) => run(() => watch.transfer(id))}
                    onKick={(id) => run(() => watch.kick(id))}
                  />
                ))}
              </ul>
              {you.isOwner && !isHost ? (
                <button type="button" className="party-btn small ghost" disabled={busy} onClick={() => run(() => watch.transfer(meId))} data-testid="watch-reclaim">Take back host</button>
              ) : null}
            </section>
            {canManage ? (
              <section className="vox-watch-settings" data-testid="watch-host-settings">
                <div className="party-kicker">Session settings</div>
                <label className="vox-watch-check">
                  <input
                    type="checkbox"
                    checked={activity.controlMode === 'everyone'}
                    disabled={busy}
                    onChange={(e) => run(() => watch.setSettings({ controlMode: e.target.checked ? 'everyone' : 'host' }))}
                    data-testid="watch-collaborative"
                  />
                  <span>
                    <strong>Collaborative mode</strong>
                    <span className="party-muted">Everyone can play, pause, seek and add videos. Off: only the host controls playback.</span>
                  </span>
                </label>
                {activity.voice ? (
                  <label className="party-setting">
                    <span>Who can find it</span>
                    <select value={activity.audience} onChange={(e) => run(() => watch.setAudience(e.target.value))} data-testid="watch-audience" disabled={busy}>
                      <option value="channel">People who can join #{activity.voice.channelName}, and anyone with the code</option>
                      <option value="invite">Only people with the code</option>
                    </select>
                  </label>
                ) : null}
                <div className="party-row">
                  <button type="button" className="party-btn small ghost" disabled={busy} onClick={() => run(() => watch.resetCode())} data-testid="watch-new-code" title="The current code and link stop working; people already here stay">
                    New invite code
                  </button>
                  {confirmEnd ? (
                    <>
                      <button type="button" className="party-btn small danger" disabled={busy} data-testid="watch-end-confirm" onClick={() => run(async () => { if (await watch.end()) onExit('You ended the watch session.'); })}>
                        End for everyone
                      </button>
                      <button type="button" className="party-btn small ghost" onClick={() => setConfirmEnd(false)}>Cancel</button>
                    </>
                  ) : (
                    <button type="button" className="party-btn small ghost danger" onClick={() => setConfirmEnd(true)} data-testid="watch-end">End session</button>
                  )}
                </div>
              </section>
            ) : null}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
